import { Record, List, Map, getIn } from "immutable";
import { addressFromJSON, IAddressData } from "common/records/Address";
import { PreferenceRecord, IPreferenceData, preferencesFromJSON } from "src/common/records/Preference";
import { fromJSON as settingsFromJSON, SettingsRecord, ISettingsData } from "common/records/Settings";
import { RootFolderNames } from "common/records/Folder";
import { IOrgOverviewData, IOrgOverviewRecord, defaultOrgOverviewProps } from "common/records/OrgOverview";
import { DropdownRecord, IDropdownData, fromJSON as dropdownFromJSON } from "common/components/Dropdown/Dropdown";
import { IJobStateData, JobStateRecord, jobStateFromJSON } from "common/records/JobState/JobState";
import { Nullable } from "common/records";
import {
  contractLanguageFromJSON,
  ContractLanguageRecord,
  IContractLanguageData,
} from "common/records/ContractLanguage";

export const orgFromJSON = (json: Partial<IOrgData>): OrgRecord => {
  const record: IOrgRecord = { ...(json as any) };

  if (json.preferences) {
    record.preferences = preferencesFromJSON(json.preferences);
  }

  if (json.settings) {
    record.settings = settingsFromJSON(json.settings);
  }

  if (json.lead_sources) {
    record.lead_sources = List<string>(json.lead_sources);
  } else {
    record.lead_sources = List<string>();
  }

  if (json.job_types) {
    record.job_types = List(json.job_types.map((jobType) => dropdownFromJSON(jobType)));
  }

  if (json.job_states) {
    record.job_states = jobStateFromJSON(json.job_states);
  }

  if (json.events) {
    record.events = List(json.events);
  }

  if (json.address) {
    record.address = addressFromJSON(json.address);
  }

  if (json.activated_price_list) {
    record.activated_price_list_id = json.activated_price_list.id;
    record.current_price_list_id = json.activated_price_list.price_list_id;
  }

  if (json.activated_inspection_list) {
    record.activated_inspection_list_id = json.activated_inspection_list.id;
  }

  if (json.emails) {
    record.emails = List(json.emails);
  }

  if (json.contract_languages) {
    record.contract_languages = List(json.contract_languages.map((l) => contractLanguageFromJSON(l)));
  }

  if (json.images?.length) {
    record.image_ids = List(json.images.map((image) => image.id));
  }

  if (json.logo) {
    record.logo_id = json.logo.id;
  }
  if (json.default_cover_image) {
    record.default_cover_image_id = json.default_cover_image.id;
  }
  if (json.custom_signature_email_image) {
    record.custom_signature_email_image_id = json.custom_signature_email_image.id;
  }

  if (json.folders) {
    record.folders = Map<RootFolderNames, number>(json.folders);
  }

  if (json.errors) {
    record.errors = List(json.errors);
  }

  return new OrgRecord(record);
};

export const getPrefConfig = (org: OrgRecord, pref: string[]): any => {
  return org.getIn(["preferences", "config"].concat(pref));
};

export const orgNameAddress = (org: OrgRecord): string => {
  const address = org.address;
  let cityState = "";
  if (address.city && address.state) {
    cityState = ` (${address.city}, ${address.state})`;
  } else if (address.city) {
    cityState = ` (${address.city})`;
  } else if (address.state) {
    cityState = ` (${address.state})`;
  }
  return `${org.name}${cityState}`;
};

export const defaultUserAcl = (org: OrgRecord) => {
  return getIn(org, ["settings", "config", "user_acl", "default"], "salesrep");
};

export interface IOrgPostData {
  id: number;
  parent_id: number;
  name: string;
  ancestral_name: string;
  phone_number: string;
  website: string;
  status: string;
  metadata: any;
  "prevent_login?": boolean;
  base36_id: string;
  job_count: number;
  org_count: number;
  user_count: number;
  descendants_count: number;
  url: string;
  settings_attributes?: ISettingsData;
  preferences_attributes?: IPreferenceData;
  address_attributes?: IAddressData;
  contract_languages_attributes?: any;
}

export interface IOrgData extends IOrgOverviewData {
  settings: ISettingsData;
  preferences: IPreferenceData;
  folders: { [key in RootFolderNames]?: number };
  job_types: IDropdownData[];
  job_states: IJobStateData;
  contract_languages: IContractLanguageData[];
  errors: string[];
}

export interface IOrgRecord extends IOrgOverviewRecord {
  current_price_list_id: Nullable<number>;
  settings: SettingsRecord;
  preferences: PreferenceRecord;
  folders: Map<RootFolderNames, number>;
  job_types: List<DropdownRecord>;
  job_states: JobStateRecord;
  contract_languages: List<ContractLanguageRecord>;
  errors: List<string>;
}

export const defaultOrgProps = {
  ...defaultOrgOverviewProps,
  current_price_list_id: null as Nullable<number>,
  settings: settingsFromJSON({}),
  preferences: preferencesFromJSON({}),
  folders: Map<RootFolderNames, number>(),
  job_types: List<DropdownRecord>(),
  job_states: jobStateFromJSON({}),
  contract_languages: List<ContractLanguageRecord>(),
  errors: List<string>(),
};

export class OrgRecord extends Record(defaultOrgProps) implements IOrgRecord {}
